// src/components/CustomNavbar.js - SMOOTH DESKTOP HOVER FIX
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../services/api';
import './CustomNavbar.css';

const CustomNavbar = () => {
  const [expanded, setExpanded] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [siteHeader, setSiteHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  // Handle responsive breakpoints
  const checkResponsive = useCallback(() => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    
    if (mobile) {
      setActiveDropdown(null);
    }
  }, []);

  // Fetch site header data
  const fetchSiteHeader = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/site_header.php`);
      if (response.data.success) {
        setSiteHeader(response.data.data);
      } else {
        setSiteHeader({
          college_name: 'VTECH CODERS',
          organization_name: 'VTECH CODERS',
          tagline: 'We Code Your IDEAS.',
          phone: '+91 9886631818',
          email: 'info@vtechcoders.com',
          logo_image: '',
          logo_icon: 'fas fa-school'
        });
      }
    } catch (error) {
      setSiteHeader({
        college_name: 'VTECH CODERS DHARWAD',
        organization_name: 'VTECH CODERS',
        tagline: 'We Code Your IDEAS.',
        phone: '+91 9886631818',
        email: 'info@vtechcoders.com',
        logo_image: '',
        logo_icon: 'fas fa-school'
      });
    }
  };

  // Fetch menu items
  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/menu.php`);
      
      if (response.data && response.data.success && response.data.data) {
        const menuData = response.data.data
          .filter(item => item.is_active === 1 || item.is_active === "1")
          .map(item => ({
            ...item,
            id: item.id.toString(),
            hasSubmenu: (item.subMenu && item.subMenu.length > 0) || item.has_submenu === 1,
            subMenu: (item.subMenu || []).filter(sub => 
              sub.is_active === 1 || sub.is_active === "1"
            )
          }))
          .sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0));
        
        setMenuItems(menuData);
      } else {
        setMenuItems([]);
      }
    } catch (error) {
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems();
    fetchSiteHeader();
    checkResponsive();

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      checkResponsive();
      if (window.innerWidth >= 768) {
        setExpanded(false);
        setActiveDropdown(null);
      }
    };

    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    // Close dropdowns when pressing Escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [checkResponsive]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path, isExternal = false, e) => {
    if (e) e.preventDefault();
    
    if (isMobile) {
      setExpanded(false);
      setActiveDropdown(null);
    }
    
    if (isExternal === true || isExternal === "1" || isExternal === 1) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  };

  // ===== SMOOTH DESKTOP HOVER HANDLERS =====
  const handleMouseEnter = useCallback((itemId) => {
    if (!isMobile) {
      // Clear any pending timeout
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
        dropdownTimeoutRef.current = null;
      }
      // Immediately show dropdown
      setActiveDropdown(itemId);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      // Small delay before hiding to allow moving to dropdown
      dropdownTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150); // Reduced delay for smoother experience
    }
  }, [isMobile]);

  const handleDropdownMouseEnter = useCallback(() => {
    if (!isMobile && dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
  }, [isMobile]);

  const handleDropdownMouseLeave = useCallback(() => {
    if (!isMobile) {
      // Hide dropdown after leaving both menu item and dropdown
      dropdownTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 100);
    }
  }, [isMobile]);

  const toggleMobileMenu = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setActiveDropdown(null);
    }
  };

  const toggleMobileSubmenu = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  // Render mobile menu items
  const renderMobileMenuItem = (item) => {
    const active = isActive(item.path);
    const hasSubmenu = item.hasSubmenu && item.subMenu && item.subMenu.length > 0;
    const isDropdownOpen = activeDropdown === item.id;
    const isExternal = item.is_external === "1" || item.is_external === 1;

    return (
      <div key={item.id} className={`mobile-nav-item ${isDropdownOpen ? 'active' : ''}`}>
        <button
          className={`mobile-nav-link ${active ? 'active' : ''} ${hasSubmenu ? 'has-dropdown' : ''}`}
          onClick={(e) => {
            if (hasSubmenu) {
              e.preventDefault();
              toggleMobileSubmenu(item.id);
            } else {
              handleNavigation(item.path, isExternal, e);
            }
          }}
          aria-expanded={isDropdownOpen}
          aria-haspopup={hasSubmenu}
        >
          <span className="mobile-nav-content">
            {item.icon && <i className={`${item.icon} me-2`} />}
            <span className="mobile-nav-text">{item.label}</span>
          </span>
          {hasSubmenu && (
            <i className={`mobile-dropdown-icon fas fa-chevron-${isDropdownOpen ? 'up' : 'down'}`} />
          )}
          {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
        </button>

        {hasSubmenu && (
          <div 
            className={`mobile-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
            ref={isDropdownOpen ? dropdownRef : null}
          >
            {item.subMenu.map((subItem) => {
              const isSubActive = isActive(subItem.path);
              const isSubExternal = subItem.is_external === "1" || subItem.is_external === 1;

              return (
                <button
                  key={subItem.id}
                  className={`mobile-dropdown-item ${isSubActive ? 'active' : ''}`}
                  onClick={(e) => {
                    handleNavigation(subItem.path, isSubExternal, e);
                    setExpanded(false);
                    setActiveDropdown(null);
                  }}
                >
                  {subItem.icon && subItem.icon !== 'fas fa-link' && (
                    <i className={`${subItem.icon} me-2`} />
                  )}
                  <span className="mobile-dropdown-text">{subItem.label}</span>
                  {isSubExternal && <i className="fas fa-external-link-alt ms-2 small" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render desktop menu items with SMOOTH hover handling
  const renderDesktopMenuItem = (item) => {
    const active = isActive(item.path);
    const hasSubmenu = item.hasSubmenu && item.subMenu && item.subMenu.length > 0;
    const isDropdownOpen = activeDropdown === item.id;
    const isExternal = item.is_external === "1" || item.is_external === 1;

    if (hasSubmenu) {
      return (
        <div
          key={item.id}
          className="desktop-nav-item dropdown"
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className={`desktop-nav-link dropdown-toggle ${active ? 'active' : ''}`}
            onClick={(e) => {
              if (isMobile) {
                e.preventDefault();
                toggleMobileSubmenu(item.id);
              } else {
                handleNavigation(item.path, isExternal, e);
              }
            }}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <span className="desktop-nav-content">
              {item.icon && <i className={`${item.icon} me-2`} />}
              <span className="desktop-nav-text">{item.label}</span>
            </span>
            <i className={`desktop-dropdown-icon fas fa-chevron-${isDropdownOpen ? 'up' : 'down'}`} />
            {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
          </button>

          {hasSubmenu && (
            <div 
              className={`desktop-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
              aria-labelledby={`menu-${item.id}`}
              ref={isDropdownOpen ? dropdownRef : null}
              style={{
                // Ensure smooth transition
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
              }}
            >
              {item.subMenu.map((subItem) => {
                const isSubActive = isActive(subItem.path);
                const isSubExternal = subItem.is_external === "1" || subItem.is_external === 1;

                return (
                  <button
                    key={subItem.id}
                    className={`desktop-dropdown-item ${isSubActive ? 'active' : ''}`}
                    onClick={(e) => {
                      handleNavigation(subItem.path, isSubExternal, e);
                      setActiveDropdown(null);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {subItem.icon && subItem.icon !== 'fas fa-link' && (
                      <i className={`${subItem.icon} me-2`} />
                    )}
                    <span className="desktop-dropdown-text">{subItem.label}</span>
                    {isSubExternal && <i className="fas fa-external-link-alt ms-2 small" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div 
        key={item.id} 
        className="desktop-nav-item"
        onMouseEnter={() => {
          // Close any open dropdowns when hovering over non-dropdown items
          if (activeDropdown && !isMobile) {
            setActiveDropdown(null);
          }
        }}
      >
        <button
          className={`desktop-nav-link ${active ? 'active' : ''}`}
          onClick={(e) => handleNavigation(item.path, isExternal, e)}
        >
          {item.icon && <i className={`${item.icon} me-2`} />}
          <span className="desktop-nav-text">{item.label}</span>
          {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
        </button>
      </div>
    );
  };

  // Header component
  const renderHeader = () => (
    <div className="navbar-header bg-white">
      <Container fluid>
        <Row className="align-items-center">
          <Col xs={3} sm={3} md={2} lg={2} xl={2} className="p-0">
            <div className="logo-container-left">
              {siteHeader?.logo_image ? (
                <img
                  src={`${UPLOAD_BASE_URL}${siteHeader.logo_image}`}
                  alt={siteHeader.college_name || 'School Logo'}
                  className="navbar-logo"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="logo-icon-container">
                        <i class="${siteHeader?.logo_icon || 'fas fa-school'} navbar-logo-icon"></i>
                      </div>
                    `;
                  }}
                />
              ) : (
                <div className="logo-icon-container">
                  <i className={`${siteHeader?.logo_icon || 'fas fa-school'} navbar-logo-icon`} />
                </div>
              )}
            </div>
          </Col>
          
          <Col xs={9} sm={9} md={10} lg={10} xl={10} className="p-0">
            <div className="navbar-brand-content-center">
              {siteHeader?.organization_name && (
                <div className="navbar-organization-wrapper">
                  <p className="navbar-organization" title={siteHeader.organization_name}>
                    {siteHeader.organization_name}
                  </p>
                </div>
              )}
              
              <div className="navbar-college-name-wrapper">
                <h1 className="navbar-college-name" title={siteHeader?.college_name}>
                  {siteHeader?.college_name || 'V TECH CODERS INFORMATION SCHOOL NAME DHARWAD'}
                </h1>
              </div>
              
              {siteHeader?.tagline && (
                <div className="navbar-tagline-wrapper">
                  <p className="navbar-tagline" title={siteHeader.tagline}>
                    {siteHeader.tagline}
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );

  if (loading) {
    return (
      <div className="navbar-header bg-white">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={12} className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <>
      {renderHeader()}

      {apiError && (
        <div className="alert alert-warning alert-dismissible fade show m-2" role="alert">
          <strong>API Error:</strong> {apiError}
          <button type="button" className="btn-close" onClick={() => setApiError(null)}></button>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobile && expanded && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setExpanded(false)}
        />
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <>
          <nav className={`mobile-menu-container ${expanded ? 'open' : ''}`}>
            <div className="mobile-menu-header">
              <h5>Menu</h5>
              <button
                className="mobile-menu-close"
                onClick={() => setExpanded(false)}
                aria-label="Close menu"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            
            <div className="mobile-menu-items">
              {menuItems.length > 0 ? (
                menuItems.map(renderMobileMenuItem)
              ) : (
                <div className="no-menu-items">
                  <i className="fas fa-bars fa-2x mb-3" />
                  <p>No menu items</p>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <nav className={`mobile-sticky-nav ${scrolled ? 'scrolled' : ''}`}>
            <Container fluid>
              <div className="d-flex justify-content-center">
                <button
                  className="mobile-menu-toggle"
                  onClick={toggleMobileMenu}
                  aria-expanded={expanded}
                  aria-label="Toggle navigation"
                >
                  <i className={`fas fa-${expanded ? 'times' : 'bars'} me-2`} />
                  <span>MENU</span>
                </button>
              </div>
            </Container>
          </nav>
        </>
      )}

      {/* Desktop/Tablet Navigation */}
      {!isMobile && (
        <nav 
          className={`desktop-navbar ${scrolled ? 'scrolled' : ''}`}
          ref={menuRef}
        >
          <Container fluid className="px-0">
            <div className="desktop-navbar-container">
              {menuItems.length > 0 ? (
                <div className="desktop-navbar-menu">
                  {menuItems.map(renderDesktopMenuItem)}
                </div>
              ) : (
                <div className="no-menu-items-desktop">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  No menu items available
                </div>
              )}
            </div>
          </Container>
        </nav>
      )}
    </>
  );
};

export default CustomNavbar;